import axios from 'axios';

// Build API URL from environment variables
const buildApiUrl = () => {
  // Use the configured API base URL, defaulting to relative path for embedded setup
  const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
  
  // If it's a relative URL and we have separate backend config, build full URL
  if (baseUrl === '/api/v1' && process.env.REACT_APP_BACKEND_HOST) {
    const protocol = process.env.REACT_APP_BACKEND_PROTOCOL || 'http';
    const host = process.env.REACT_APP_BACKEND_HOST;
    const port = process.env.REACT_APP_BACKEND_PORT || '8080';
    return `${protocol}://${host}:${port}/api/v1`;
  }
  
  return baseUrl;
};

const API_BASE_URL = buildApiUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use((config) => {
  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      console.error('Unauthorized access - token may be expired');
    }
    return Promise.reject(error);
  }
);

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  files?: string[];
  meta?: {
    citations?: Citation[];
    [key: string]: any;
  };
}

export interface Citation {
  title: string;
  uri: string;
}

export interface ChatResponse {
  content: string;
  citations?: Citation[];
}

export interface ChatRequest {
  message: ChatMessage;
  history: ChatMessage[];
}

export interface UploadResponse {
  fileId: string;
  fileName: string;
  size: number;
  url: string;
}

export const sendMessage = async (request: ChatRequest, token: string): Promise<ChatResponse> => {
  const response = await apiClient.post('/chat', request, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const uploadFile = async (file: File, token: string): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/upload', formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const downloadFile = async (fileId: string, token: string): Promise<Blob> => {
  const response = await apiClient.get(`/files/${fileId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: 'blob',
  });
  return response.data;
};

export interface Prompt {
  id: string;
  title: string;
  description: string;
}

export interface PromptsResponse {
  prompts: Prompt[];
}

export const getPrompts = async (): Promise<PromptsResponse> => {
  const response = await apiClient.get('/prompts');
  return response.data;
};

export default apiClient;